import { Table, Checkbox, Input, TableProps, Button, Space, message } from 'antd';
import { EditOutlined, CheckOutlined } from '@ant-design/icons'; // Import the pencil icon
import moment from 'moment';
import React, { useState } from 'react';
import { CONTEXT_MAP } from 'utils/constants';
import { LEGACY, LEGACY_TEXT } from './const';
import {
  Dimension,
  DEACTIVATE_OPERATION,
  ACTIVATE_OPERATION,
  DELETE_OPERATION,
  CHANGE_NAME_OPERATION,
} from '../utils/types';
import * as Style from '../utils/styled';

// Helper functions for sorting and rendering
export const sortStrings = (a: string | undefined, b: string | undefined, sortOrder: string) => {
  const valA = a?.trim() || '';
  const valB = b?.trim() || '';
  if (!valA && !valB) return 0;
  if (!valA) return sortOrder === 'ascend' ? 1 : -1;
  if (!valB) return sortOrder === 'ascend' ? -1 : 1;
  return valA.localeCompare(valB);
};

// Main Component
const DimensionTable = ({ data, handleEdition, isAdminUser, maxInfo, timeZone }) => {
  // State for managing the editing state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [dimensionName, setDimensionName] = useState<string>('');

  const clearEditing = () => {
    setEditingKey(null);
    setDimensionName('');
  };

  const handleChangeName = (record: Dimension) => {
    if (dimensionName.includes(LEGACY)) {
      message.error(LEGACY_TEXT);
      clearEditing();
      return;
    }
    const newName = dimensionName.trim();
    if (newName && newName !== record.name) {
      handleEdition(record, newName, CHANGE_NAME_OPERATION); // Call the handler to change the name
    }
    clearEditing(); // Exit editing state
  };

  const columns: TableProps<Dimension>['columns'] = [
    {
      width: '15%',
      title: 'Dimension',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b, sortOrder) => sortStrings(a.name, b.name, sortOrder as string),
      sortDirections: ['ascend', 'descend'],
      render: (dimensionName: string, record: Dimension) =>
        dimensionName.includes(LEGACY) || !isAdminUser ? (
          <span>{dimensionName}</span>
        ) : editingKey === record.name ? (
          <Space>
            <Input
              defaultValue={dimensionName}
              onChange={(e) => setDimensionName(e.target.value)}
              autoFocus
            />
            <Button
              icon={<CheckOutlined style={{ cursor: 'pointer', color: 'blue' }} />}
              onClick={() => {
                handleChangeName(record);
              }}
            ></Button>
          </Space>
        ) : (
          <Style.DimensionDiv>
            <Style.DimensionSpan>{dimensionName}</Style.DimensionSpan>
            <EditOutlined
              style={{ marginLeft: 8, cursor: 'pointer', color: 'blue' }}
              onClick={() => setEditingKey(record.name)}
            ></EditOutlined>
          </Style.DimensionDiv>
        ),
    },
    {
      title: 'Tag Key',
      dataIndex: 'tag_key',
      key: 'tag_key',
      sorter: (a, b, sortOrder) => sortStrings(a.tag_key, b.tag_key, sortOrder as string),
      sortDirections: ['ascend', 'descend'],
      render: (value: string) => (value?.trim() ? value : '-- --'), // Render '--' if tag_key is empty
    },
    {
      title: () => (
        <>
          Video (
          <span
            style={{
              color: maxInfo.activeEIDimension >= maxInfo.maxEIDimension ? 'red' : 'inherit',
            }}
          >
            {maxInfo.activeEIDimension}
          </span>
          /{maxInfo.maxEIDimension})
        </>
      ),
      dataIndex: 'ei',
      key: 'ei',
      sorter: (a, b, sortOrder) =>
        sortStrings(a.ei.toString(), b.ei.toString(), sortOrder as string),
      sortDirections: ['ascend', 'descend'],
      render: (text: boolean, record: Dimension) => (
        <Checkbox
          disabled={!isAdminUser}
          checked={text}
          onChange={() =>
            handleEdition(record, CONTEXT_MAP.EI, text ? DEACTIVATE_OPERATION : ACTIVATE_OPERATION)
          }
        />
      ),
    },
    {
      title: () => (
        <>
          Ads (
          <span
            style={{
              color: maxInfo.activeAIDimension >= maxInfo.maxAIDimension ? 'red' : 'inherit',
            }}
          >
            {maxInfo.activeAIDimension}
          </span>
          /{maxInfo.maxAIDimension})
        </>
      ),
      dataIndex: 'ai',
      key: 'ai',
      sorter: (a, b, sortOrder) =>
        sortStrings(a.ai.toString(), b.ai.toString(), sortOrder as string),
      sortDirections: ['ascend', 'descend'],
      render: (text: boolean, record: Dimension) => (
        <Checkbox
          disabled={!isAdminUser}
          checked={text}
          onChange={() =>
            handleEdition(record, CONTEXT_MAP.AI, text ? DEACTIVATE_OPERATION : ACTIVATE_OPERATION)
          }
        />
      ),
    },
    {
      title: 'Last Update',
      dataIndex: 'operation',
      key: 'operation',
      sorter: (a, b, sortOrder) => sortStrings(a.operation, b.operation, sortOrder as string),
      sortDirections: ['ascend', 'descend'],
      render: (value: string) => (value?.trim() ? value : '-- --'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: (a, b, sortOrder) => {
        const dateA = a.updated_at ? Date.parse(a.updated_at) : 0;
        const dateB = b.updated_at ? Date.parse(b.updated_at) : 0;
        if (!dateA && !dateB) return 0;
        if (!dateA) return sortOrder === 'ascend' ? 1 : -1;
        if (!dateB) return sortOrder === 'ascend' ? -1 : 1;
        return dateA - dateB;
      },
      render: (value: string) => {
        if (!value?.trim()) return '-- --';
        const offset = typeof timeZone === 'number' ? timeZone : 0;
        return moment(value).utcOffset(offset).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: (a, b, sortOrder) => sortStrings(a.updated_by, b.updated_by, sortOrder as string),
      sortDirections: ['ascend', 'descend'],
      render: (value: string) => (value?.trim() ? value : '-- --'),
    },
    ...(isAdminUser
      ? [
          {
            title: 'Action',
            key: 'action',
            render: (text: boolean, record: Dimension) => (
              <a onClick={() => handleEdition(record, 'name', DELETE_OPERATION)}>Delete</a>
            ),
          },
        ]
      : []),
  ];

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey="id"
      scroll={{ y: 'calc(100vh - 300px)' }}
      pagination={{
        pageSize: 50,
        hideOnSinglePage: true,
        showQuickJumper: false,
        showSizeChanger: false,
      }}
    />
  );
};

export default DimensionTable;
